/**
 * GiftPlansInvitePage — iter107
 * ─────────────────────────────────────────────────────────────
 * Shareable landing that combines a personal gift card with the
 * CARTAL pricing plans (Basic / Plus / Pro), showing the 50% launch
 * discount on paid tiers.
 *
 * Route: /gift/plans
 * The original `/gift` teaser page stays untouched.
 *
 * Layout mirrors the reference designs shared by the founder:
 *   • Left half: black gift card with gold ribbon + recipient name input
 *   • Right half: pricing tiles with launch discount ribbons
 *   • Bottom trust strip: 4 icons + labels
 *   • Language toggle (AR ⇆ EN) — both languages render fully
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Star, Trophy, Shield, Headphones, RefreshCw, BadgeCheck,
  ArrowRight, Copy, Share2, Gift,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { launchPrice } from "../lib/launchDiscount";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_carvo-market/artifacts/c5durw9k_B010DD54-BD83-49AB-A5A3-946EA832B9AF.png";
const SHARE_URL = "https://cartal.sa";

// Base (pre-discount) monthly prices in SAR. `launchPrice()` halves them.
const PLANS = [
  {
    id: "plus",
    price: 19,   // iter113 — matches backend catalog
    accent: "#0ea5e9",
    accent_soft: "#7dd3fc",
    bg: "linear-gradient(135deg,#082f49 0%,#0c4a6e 55%,#075985 100%)",
    icon: Star,
    tag_ar: "للأفراد الجادّين",     tag_en: "For serious individuals",
    name_ar: "Plus للأفراد",         name_en: "Plus Individual",
    desc_ar: "بائعون أفراد بدون أسطول وطموحاتهم عالية.",
    desc_en: "For individuals without fleets and applications",
    highlight_ar: "عرض محدود",       highlight_en: "LIMITED",
    cta_ar: "اشترك في Plus",          cta_en: "Subscribe to Plus",
    cta_bg: "#0ea5e9",
  },
  {
    id: "dealer_pro",
    price: 199,
    accent: "#c9a55c",
    accent_soft: "#e8c873",
    bg: "linear-gradient(135deg,#1a1208 0%,#3d2e10 55%,#5a4615 100%)",
    icon: Trophy,
    tag_ar: "للمعارض والتجار",       tag_en: "For dealers & showrooms",
    name_ar: "Pro للتجار",           name_en: "Pro Dealer",
    desc_ar: "تجار يديرون أساطيل ويستهدفون النمو السريع.",
    desc_en: "Dealers without fleets targeting fast growth",
    popular_ar: "الأكثر طلباً",       popular_en: "MOST POPULAR",
    highlight_ar: "عرض محدود",       highlight_en: "LIMITED TIME",
    cta_ar: "اشترك في Pro",          cta_en: "Subscribe to Pro",
    cta_bg: "#c9a55c",
  },
  {
    id: "basic",
    price: 0,
    accent: "#8a6f2e",
    accent_soft: "#c9a55c",
    bg: "linear-gradient(135deg,#1a1a1a 0%,#0a0a0a 100%)",
    icon: Sparkles,
    tag_ar: "باقة البداية",          tag_en: "STARTER PLAN",
    name_ar: "أساسي",                 name_en: "Basic Plan",
    desc_ar: "للأفراد الذين يجرّبون البيع لأول مرة.",
    desc_en: "For individuals selling for the first time",
    starter_ar: "ابدأ مجاناً",         starter_en: "START FOR FREE",
    activity_ar: "باقة الأنشطة",       activity_en: "Activity Package",
    cta_bg: "transparent",
  },
];

const TRUST = [
  { icon: Shield,      ar_h: "أمن وموثوق",     en_h: "SECURE & TRUSTED",
    ar_s: "حماية بياناتك وخصوصيتك", en_s: "Your data & privacy are protected" },
  { icon: Headphones,  ar_h: "دعم فني 24/7",    en_h: "24/7 SUPPORT",
    ar_s: "مساعدتك في أي وقت",       en_s: "We're here to help anytime" },
  { icon: RefreshCw,   ar_h: "تحديثات مستمرة",  en_h: "CONTINUOUS UPDATES",
    ar_s: "ميزات جديدة باستمرار",     en_s: "New features, always improving" },
  { icon: BadgeCheck,  ar_h: "لا رسوم خفية",    en_h: "NO HIDDEN FEES",
    ar_s: "شفافية تامة",              en_s: "100% transparent pricing" },
];

export default function GiftPlansInvitePage() {
  const { lang, setLang } = useI18n();
  const [recipient, setRecipient] = useState("");
  const [billing, setBilling] = useState("monthly"); // monthly | yearly

  const yearlyDiscountPct = 15;
  const priceFor = (p) => {
    if (!p.price) return 0;
    return billing === "yearly"
      ? Math.round(p.price * 12 * (1 - yearlyDiscountPct / 100))
      : p.price;
  };

  const inviteUrl = useMemo(() => {
    const u = new URL(SHARE_URL + "/gift/plans");
    if (recipient.trim()) u.searchParams.set("to", recipient.trim());
    if (lang) u.searchParams.set("lang", lang);
    return u.toString();
  }, [recipient, lang]);

  const copyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    toast.success(lang === "ar" ? "تم نسخ الرابط" : "Link copied");
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: lang === "ar" ? "دعوة كارتال" : "CARTAL invitation",
          text: recipient.trim()
            ? (lang === "ar" ? `${recipient}، هذه دعوتك الخاصة إلى كارتال.` : `${recipient}, your special CARTAL invitation.`)
            : (lang === "ar" ? "دعوة خاصة إلى كارتال." : "A special invitation to CARTAL."),
          url: inviteUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const ar = lang === "ar";
  const dir = ar ? "rtl" : "ltr";

  return (
    <div
      dir={dir}
      className="min-h-screen w-full py-8 sm:py-12 px-3 sm:px-6"
      style={{
        background: "radial-gradient(circle at 50% 30%, #1a1208 0%, #0a0806 55%, #050505 100%)",
      }}
      data-testid="gift-plans-page"
    >
      {/* Top toolbar — language + share */}
      <div className="max-w-[1500px] mx-auto flex items-center justify-between mb-6 gap-2 flex-wrap">
        <Link to="/gift" className="text-xs text-[#c9a55c] hover:text-[#f5d478] inline-flex items-center gap-1" data-testid="back-to-old-gift">
          <ArrowRight className={`w-4 h-4 ${ar ? "" : "rotate-180"}`} />
          {ar ? "عودة إلى بطاقة الهدية الكلاسيكية" : "Back to classic gift card"}
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setLang(ar ? "en" : "ar")}
            className="text-[11px] font-bold rounded-full px-3 py-1.5 border"
            style={{ borderColor: "#c9a55c", color: "#f5d478", background: "rgba(201,165,92,0.08)" }}
            data-testid="toggle-lang"
          >
            {ar ? "English" : "العربية"}
          </button>
          <button
            type="button"
            onClick={copyLink}
            className="text-[11px] font-bold rounded-full px-3 py-1.5 border inline-flex items-center gap-1"
            style={{ borderColor: "#c9a55c", color: "#f5d478", background: "rgba(201,165,92,0.08)" }}
            data-testid="copy-link"
          >
            <Copy className="w-3 h-3" /> {ar ? "نسخ الرابط" : "Copy link"}
          </button>
          <button
            type="button"
            onClick={share}
            className="text-[11px] font-bold rounded-full px-3 py-1.5 inline-flex items-center gap-1"
            style={{ background: "linear-gradient(135deg,#f5d478,#c9a55c)", color: "#0a0a0a" }}
            data-testid="share-link"
          >
            <Share2 className="w-3 h-3" /> {ar ? "مشاركة" : "Share"}
          </button>
        </div>
      </div>

      {/* Main two-column layout */}
      <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-6 lg:gap-8">
        {/* Left — Gift card */}
        <div
          className="relative rounded-3xl p-5 sm:p-6 lg:p-8 overflow-hidden"
          style={{
            background: "linear-gradient(180deg,#0d0a05 0%,#0a0806 100%)",
            border: "1.5px solid rgba(201,165,92,0.35)",
            boxShadow: "0 30px 80px -20px rgba(201,165,92,0.15)",
          }}
          data-testid="gift-card-panel"
        >
          {/* Header pill */}
          <div className="text-center mb-4">
            <div className="inline-flex flex-col items-center">
              <div className="flex items-center gap-2 justify-center mb-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#c9a55c" }} />
                <span className="font-display text-xl sm:text-2xl font-black tracking-wide" style={{ color: "#f5d478" }}>
                  {ar ? "بطاقة هدية" : "GIFT CARD"}
                </span>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#c9a55c" }} />
              </div>
              <div className="text-[10px] tracking-[0.35em] font-bold" style={{ color: "#c9a55c" }}>
                {ar ? "GIFT CARD" : "بطاقة هدية"}
              </div>
            </div>
          </div>

          {/* Card visual */}
          <div
            className="relative rounded-2xl aspect-[16/10] overflow-hidden mb-6"
            style={{
              background: "linear-gradient(140deg,#0a0806 0%,#141008 55%,#1a1208 100%)",
              border: "1px solid rgba(201,165,92,0.35)",
            }}
          >
            {/* Gold diagonal accent */}
            <div
              className="absolute top-0 end-0 w-1/3 h-full opacity-70"
              style={{
                background: "linear-gradient(135deg,transparent 20%,#c9a55c 60%,#8a6f2e 100%)",
                clipPath: "polygon(60% 0, 100% 0, 100% 100%, 20% 100%)",
              }}
            />
            {/* Gold ribbon (top-left) */}
            <div className="absolute top-3 start-3 sm:top-5 sm:start-5 w-14 sm:w-20">
              <div
                className="w-full h-3 sm:h-4 rounded-t-md"
                style={{ background: "linear-gradient(90deg,#f5d478,#c9a55c,#8a6f2e)" }}
              />
              <div
                className="w-full h-14 sm:h-20"
                style={{ background: "linear-gradient(180deg,#c9a55c,#8a6f2e)" }}
              />
              <div
                className="w-full h-2 sm:h-3"
                style={{
                  clipPath: "polygon(0 0, 50% 100%, 100% 0)",
                  background: "linear-gradient(180deg,#8a6f2e,#5a4615)",
                }}
              />
            </div>

            {/* Centred logo + hero */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <img src={LOGO_URL} alt="CARTAL" className="h-14 sm:h-20 object-contain mb-1" />
              <div className="text-[9px] sm:text-[10px] tracking-[0.35em] font-bold" style={{ color: "#c9a55c" }}>
                DRIVE MORE · LIVE BETTER
              </div>
              <div className="mt-3 sm:mt-5 font-display text-base sm:text-xl font-black" style={{ color: "#f5d478" }} dir="rtl">
                قِد أكثر. عِش أفضل.
              </div>
              <div className="mt-0.5 italic text-xs sm:text-sm" style={{ color: "#c9a55c" }}>
                Drive more. Live better.
              </div>
            </div>
          </div>

          {/* Recipient input */}
          <div className="text-center mb-3">
            <div className="text-[10px] tracking-[0.3em] font-bold mb-1.5" style={{ color: "#c9a55c" }}>
              {ar ? "دعوة خاصة إلى" : "A SPECIAL GIFT FOR"}
            </div>
            <div className="text-[9px] tracking-[0.3em]" style={{ color: "rgba(201,165,92,0.6)" }}>
              {ar ? "A SPECIAL GIFT FOR" : "دعوة خاصة إلى"}
            </div>
          </div>
          <input
            type="text"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder={ar ? "اسم المستلم" : "RECIPIENT NAME"}
            className="w-full text-center font-display text-lg sm:text-xl font-bold py-3 px-4 mb-6 rounded-xl outline-none"
            style={{
              background: "transparent",
              color: "#f5d478",
              border: "2px dashed rgba(201,165,92,0.5)",
              letterSpacing: "0.08em",
            }}
            data-testid="recipient-name-input"
          />

          {/* Official platform CTA */}
          <div className="text-center">
            <div className="text-[10px] tracking-[0.3em] font-bold mb-2" style={{ color: "#c9a55c" }}>
              {ar ? "زُر المنصة الرسمية" : "OFFICIAL PLATFORM"}
            </div>
            <a
              href={inviteUrl}
              onClick={(e) => {
                // Same-domain navigation with recipient encoded — open in new tab
                if (typeof window !== "undefined" && window.location.origin === SHARE_URL) {
                  e.preventDefault();
                  window.location.href = "/";
                }
              }}
              className="relative inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-full font-display text-xl sm:text-2xl font-black transition-transform hover:scale-[1.02]"
              style={{
                background: "linear-gradient(180deg,#f5d478 0%,#c9a55c 55%,#8a6f2e 100%)",
                color: "#0a0806",
                border: "1.5px solid #f5d478",
                boxShadow: "0 12px 40px -12px rgba(245,212,120,0.4), inset 0 1px 0 rgba(255,255,255,0.4)",
                textShadow: "0 1px 0 rgba(255,255,255,0.25)",
              }}
              data-testid="official-platform-cta"
              target="_blank" rel="noopener noreferrer"
            >
              <Sparkles className="w-5 h-5" />
              cartal.sa
            </a>
            <div className="mt-3 text-[10px] tracking-[0.3em] font-bold" style={{ color: "#c9a55c" }}>
              {ar ? "المنصة السعودية للسيارات" : "THE SAUDI PLATFORM FOR VEHICLES"}
            </div>
          </div>
        </div>

        {/* Right — Pricing */}
        <div className="flex flex-col">
          {/* Header pill */}
          <div className="text-center mb-3">
            <div
              className="inline-flex flex-col items-center px-5 py-2 rounded-full border"
              style={{ borderColor: "#c9a55c", background: "rgba(201,165,92,0.05)" }}
            >
              <span className="text-[10px] tracking-[0.25em] font-bold" style={{ color: "#f5d478" }}>
                {ar ? "عرض كارتال الحصري" : "CARTAL EXCLUSIVE OFFER"}
              </span>
              <span className="text-[9px] tracking-[0.3em]" style={{ color: "rgba(201,165,92,0.7)" }}>
                {ar ? "CARTAL EXCLUSIVE OFFER" : "عرض كارتال الحصري"}
              </span>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-center leading-tight mb-1" style={{ color: "#f5d478" }} dir="rtl">
            قارن الباقات.
          </h1>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-center leading-tight" style={{ color: "#f5d478" }} dir="rtl">
            اختر النمو الذي يناسبك.
          </h2>
          <div className="text-center text-[11px] sm:text-xs tracking-[0.15em] font-bold my-2" style={{ color: "#c9a55c" }}>
            COMPARE PLANS. CHOOSE THE GROWTH THAT FITS YOU.
          </div>
          <p className="text-center text-[11px] sm:text-xs mb-4 max-w-lg mx-auto" style={{ color: "rgba(232,225,207,0.75)" }}>
            {ar
              ? "كل ما تحتاجه لبيع وشراء وتمويل المركبات — في باقة واحدة. ابدأ مجاناً. طوّر متى شئت."
              : "Everything you need to buy, sell and finance vehicles — in one package. Start for free. Upgrade anytime."}
          </p>

          {/* Billing toggle */}
          <div className="flex justify-center mb-5">
            <div className="inline-flex rounded-full p-1" style={{ background: "rgba(201,165,92,0.1)", border: "1px solid rgba(201,165,92,0.3)" }}>
              <button
                type="button"
                onClick={() => setBilling("monthly")}
                className="px-4 py-1.5 rounded-full text-[11px] font-bold transition-colors"
                style={billing === "monthly"
                  ? { background: "#c9a55c", color: "#0a0806" }
                  : { color: "#c9a55c" }}
                data-testid="billing-monthly"
              >
                {ar ? "شهري · MONTHLY" : "MONTHLY · شهري"}
              </button>
              <button
                type="button"
                onClick={() => setBilling("yearly")}
                className="px-4 py-1.5 rounded-full text-[11px] font-bold transition-colors"
                style={billing === "yearly"
                  ? { background: "#ffffff", color: "#0a0806" }
                  : { color: "#c9a55c" }}
                data-testid="billing-yearly"
              >
                {ar ? "سنوي · وفّر 15%" : "15% OFF · YEARLY"}
              </button>
            </div>
          </div>

          {/* Plan tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4">
            {PLANS.map((p) => {
              const Icon = p.icon;
              const isFree = p.price === 0;
              const displayPrice = priceFor(p);
              const info = launchPrice(displayPrice);
              return (
                <div
                  key={p.id}
                  className="relative rounded-2xl p-4 sm:p-5 flex flex-col"
                  style={{
                    background: p.bg,
                    border: `1.5px solid ${p.accent}55`,
                    boxShadow: `0 20px 50px -20px ${p.accent}44`,
                  }}
                  data-testid={`gift-plan-${p.id}`}
                >
                  {/* Corner ribbons */}
                  {p.popular_en && (
                    <div className="absolute -top-2 start-3 rounded-md px-2 py-0.5 text-[9px] font-black uppercase tracking-wider"
                         style={{ background: p.accent, color: "#0a0806" }}>
                      {ar ? p.popular_ar : p.popular_en}
                    </div>
                  )}
                  {!isFree && info.active && (
                    <div className="absolute -top-2 end-3 rounded-md px-2 py-0.5 text-[9px] font-black uppercase tracking-wider shadow-lg"
                         style={{ background: "linear-gradient(135deg,#ef4444,#b91c1c)", color: "#fff" }}
                         data-testid={`gift-launch-ribbon-${p.id}`}>
                      {ar ? "خصم 15٪ · إطلاق" : "15% OFF · LAUNCH"}
                    </div>
                  )}
                  {isFree && (
                    <div className="absolute -top-2 end-3 rounded-md px-2 py-0.5 text-[9px] font-black uppercase tracking-wider"
                         style={{ background: p.accent, color: "#0a0806" }}>
                      {ar ? "باقة البداية" : "STARTER PLAN"}
                    </div>
                  )}

                  {/* Icon */}
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mt-4 mb-3 mx-auto"
                       style={{ background: `${p.accent}25`, border: `1.5px solid ${p.accent}` }}>
                    <Icon className="w-5 h-5" style={{ color: p.accent_soft }} />
                  </div>

                  <div className="text-center">
                    {isFree && (
                      <div className="text-[9px] tracking-[0.25em] font-bold mb-1" style={{ color: p.accent_soft }}>
                        {ar ? p.starter_ar : p.starter_en}
                      </div>
                    )}
                    <div className="text-[10px] tracking-[0.2em] font-bold mb-1" style={{ color: p.accent_soft }}>
                      {ar ? p.tag_ar : p.tag_en}
                    </div>
                    <div className="font-display text-lg sm:text-xl font-black" style={{ color: "#f7eecd" }}>
                      {ar ? p.name_ar : p.name_en}
                    </div>
                    <div className="text-[10px] mt-1 leading-snug" style={{ color: "rgba(247,238,205,0.7)" }}>
                      {ar ? p.desc_ar : p.desc_en}
                    </div>
                  </div>

                  {/* Price block */}
                  <div className="mt-3 mb-3 flex items-baseline justify-center gap-2 flex-wrap">
                    {isFree ? (
                      <>
                        <div className="font-display text-4xl font-black" style={{ color: "#f7eecd" }}>
                          {ar ? "مجاناً" : "FREE"}
                        </div>
                        <div className="text-[10px] font-bold" style={{ color: p.accent_soft }}>
                          {ar ? "دائماً" : "FOREVER"}
                        </div>
                      </>
                    ) : (
                      <>
                        {info.active && (
                          <span className="font-display text-lg line-through decoration-2"
                                style={{ color: "rgba(247,238,205,0.4)", textDecorationColor: "#ef4444" }}>
                            {info.original}
                          </span>
                        )}
                        <span className="font-display text-4xl font-black" style={{ color: "#f7eecd" }}>
                          {info.current}
                        </span>
                        <div className="text-[9px] leading-tight" style={{ color: "rgba(247,238,205,0.7)" }}>
                          <div>{ar ? "ر.س / شهرياً" : "SAR / MONTH"}</div>
                          <div className="opacity-70">{ar ? "SAR / MONTH" : "ر.س / شهرياً"}</div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Bottom pill */}
                  {!isFree && (
                    <div className="mx-auto mb-3 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[9px] font-black"
                         style={{ background: `${p.accent}20`, color: p.accent_soft, border: `1px solid ${p.accent}55` }}>
                      <span>{ar ? p.highlight_ar : p.highlight_en}</span>
                      <span className="opacity-60">·</span>
                      <span>{ar ? `فقط ${info.current} ر.س / شهرياً` : `ONLY ${info.current} SAR / MONTH`}</span>
                    </div>
                  )}

                  {/* CTA */}
                  {isFree ? (
                    <div className="mx-auto rounded-full px-4 py-2 text-[11px] font-bold border inline-flex items-center gap-1"
                         style={{ borderColor: p.accent, color: p.accent_soft }}>
                      <BadgeCheck className="w-3.5 h-3.5" /> {ar ? p.activity_ar : p.activity_en}
                    </div>
                  ) : (
                    <Link
                      to={`/checkout/moyasar?purpose=subscription&sku=${p.id === "plus" ? "individual_plus" : "dealer_pro"}`}
                      className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-xs font-black transition-transform hover:scale-[1.02]"
                      style={{ background: p.cta_bg, color: p.id === "plus" ? "#ffffff" : "#0a0806" }}
                      data-testid={`gift-cta-${p.id}`}
                    >
                      <span>{ar ? p.cta_ar : p.cta_en}</span>
                      <ArrowRight className={`w-3.5 h-3.5 ${ar ? "rotate-180" : ""}`} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="max-w-[1500px] mx-auto mt-6">
        <div
          className="rounded-2xl p-4 sm:p-5 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4"
          style={{
            background: "linear-gradient(180deg,rgba(201,165,92,0.06),rgba(201,165,92,0.02))",
            border: "1px solid rgba(201,165,92,0.35)",
          }}
          data-testid="trust-strip"
        >
          {TRUST.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.en_h} className="flex items-center gap-3">
                <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{ background: "rgba(201,165,92,0.15)", border: "1px solid rgba(201,165,92,0.4)" }}>
                  <Icon className="w-4 h-4" style={{ color: "#f5d478" }} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black tracking-wider truncate" style={{ color: "#f5d478" }}>
                    {ar ? t.ar_h : t.en_h}
                  </div>
                  <div className="text-[10px] truncate" style={{ color: "rgba(232,225,207,0.7)" }}>
                    {ar ? t.ar_s : t.en_s}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-4">
          <Link to="/gift" className="inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: "#c9a55c" }}>
            <Gift className="w-3.5 h-3.5" />
            {ar ? "بطاقة الهدية الكلاسيكية (النسخة الأصلية)" : "Classic gift card (original version)"}
          </Link>
        </div>
      </div>
    </div>
  );
}
